import { useCallback, useRef } from 'react';
import { submitWritingResult, submitSessionComplete } from '../api/practice';

function newSessionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID — RFC4122 v4 shape.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function usePractice(childId) {
  const resultsRef = useRef([]);
  const sessionIdRef = useRef(newSessionId());

  const recordCharacter = useCallback(async ({ characterId, correct, mistakes, strokeCount }) => {
    const response = await submitWritingResult({
      childId,
      characterId,
      correct,
      mistakes,
      strokeCount,
      sessionId: sessionIdRef.current,
    });

    resultsRef.current.push({
      characterId,
      correct,
      mistakes,
      strokeCount,
      progression: response.progression,
    });

    return response;
  }, [childId]);

  const finishSession = useCallback(async () => {
    const results = resultsRef.current;
    if (results.length === 0) {
      return { progression: null, sessionSize: 0, allPerfect: false };
    }

    const characterIds = results.map((r) => r.characterId);
    const allPerfect = results.every((r) => r.correct);

    const response = await submitSessionComplete({
      childId,
      characterIds,
      sessionSize: results.length,
      allPerfect,
    });

    return response;
  }, [childId]);

  const resetSession = useCallback(() => {
    resultsRef.current = [];
    sessionIdRef.current = newSessionId();
  }, []);

  const getResults = useCallback(() => resultsRef.current, []);

  return { recordCharacter, finishSession, resetSession, getResults };
}
